/**
 * Sound control for a background video — rendered by sections/jd-media-with-text-overlay.liquid.
 *
 * Background videos autoplay muted because no browser allows autoplay with sound; unmuting is only
 * ever possible from a user gesture, which is exactly what this button is.
 *
 * Delegated on the document so it survives section re-renders in the theme editor, and scoped to the
 * button's own section so several sections on one page each drive their own video.
 */

/**
 * Reflects the video's state onto the button rather than tracking it separately, so the element
 * stays the single source of truth even if something else mutes it. `aria-pressed` carries "sound is
 * on" — the stylesheet reads it to swap the icons.
 * @param {HTMLElement} button
 * @param {HTMLVideoElement} video
 */
const sync = (button, video) => {
  button.setAttribute('aria-pressed', String(!video.muted));
  button.setAttribute('aria-label', video.muted ? button.dataset.labelUnmute : button.dataset.labelMute);
};

/** @param {HTMLElement} button */
const videoFor = (button) => button.closest('.shopify-section')?.querySelector('video') ?? null;

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('[data-jd-media-sound]');
  if (!(button instanceof HTMLElement)) return;

  const video = videoFor(button);
  if (!(video instanceof HTMLVideoElement)) return;

  video.muted = !video.muted;

  /* Unmuting is worthless on a paused video, and the browser may well have refused to start it
     (data saver, or a tab that never had focus). The gesture that unmutes may also start it. */
  if (!video.muted && video.paused) video.play().catch(() => {});

  sync(button, video);
});

/* Keep the label and icon honest if the video's muted state changes on its own. `volumechange` does
   not bubble, hence the capture phase. */
document.addEventListener(
  'volumechange',
  (event) => {
    if (!(event.target instanceof HTMLVideoElement)) return;
    const button = event.target.closest('.shopify-section')?.querySelector('[data-jd-media-sound]');
    if (button instanceof HTMLElement) sync(button, event.target);
  },
  true
);
